import { useContext } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTrigger,
  Menu,
  MenuList,
  MenuPopover,
  MenuTrigger,
  ToolbarDivider,
  mergeClasses,
} from '@fluentui/react-components';

import ColorCellButton from './ColorCellButton';
import ToolbarMenuButton from '@modules/documents/components/header/toolbar/ToolbarMenuButton';
import ToolbarMenuItem from '@modules/documents/components/header/toolbar/ToolbarMenuItem';
import ToolbarButton from '@modules/documents/components/header/toolbar/ToolbarButton';
import ToolbarToggleButton from '@modules/documents/components/header/toolbar/ToolbarToggleButton';

import DocumentContext from '@modules/documents/contexts/DocumentContext.context';
import useEditorActive from '@modules/documents/hooks/useEditorActive.hook';

import { TextDirectionHorizontalRightRegular, TextDirectionVerticalRegular } from '@fluentui/react-icons';
import { useIsSafari } from '@shared/hooks';
import { Overflow, TOverflowNodeParam } from '@shared/components/Overflow';
import useToolbarButtonStyles from '@modules/documents/hooks/useToolbarButtonStyles.hook';
import InsertCaptionButton from '../caption/insert/InsertCaptionButton';
import BordersMenuButton from './BordersMenuButton';
import CellAlignmentMenuButton from './CellAlignmentMenuButton';
import { isLockedOutsideFields } from '../../extensions/TemplateLocking';

type TProps = {
  isBubble?: boolean;
};
const TableNodeTabContent = ({ isBubble = false }: TProps) => {
  const { editor } = useContext(DocumentContext);
  const classes = useToolbarButtonStyles();

  const isSafari = useIsSafari();
  const showDialog = isBubble && isSafari; // maybe the Dropdown Safari bug could be solved https://github.com/ueberdosis/tiptap/discussions/4145
  const showTooltip = !showDialog;

  const isTextRotated = useEditorActive(
    (e) => !!(e.getAttributes('tableCell')?.textRotation || e.getAttributes('tableHeader')?.textRotation)
  );

  // can() does not know about template locking: the locked body of a bound doc filters the edit.
  const canExecute = useEditorActive((e) => {
    const locked = isLockedOutsideFields(e.state);
    return {
      deleteTable: !locked && e.can().deleteTable(),
      deleteColumn: !locked && e.can().deleteColumn(),
      deleteRow: !locked && e.can().deleteRow(),
      addRowBefore: !locked && e.can().addRowBefore(),
      addRowAfter: !locked && e.can().addRowAfter(),
      addColumnBefore: !locked && e.can().addColumnBefore(),
      addColumnAfter: !locked && e.can().addColumnAfter(),
    };
  });

  // delete:
  const deleteButton = (
    <ToolbarMenuButton
      icon={
        <svg height="20" width="20" viewBox="0,0,2048,2048" focusable="false">
          <path
            type="path"
            style={{ fill: 'transparent' }}
            d="M 1354 1728 h -1226 v -1408 h 1792 v 842 l -64 64 v -394 h -512 v 330 l -64 -64 v -266 h -512 v 384 h 357 l -45 44 l 20 20 h -332 v 384 h 650 m -714 -832 h -512 v 384 h 512 m 576 -448 v -384 h -512 v 384 m 1088 -384 h -512 v 384 h 512 m -1664 -384 v 384 h 512 v -384 m -512 1280 h 512 v -384 h -512 m 1485 352 l 371 371 l -45 45 l -371 -371 l -371 371 l -45 -45 l 371 -371 l -371 -371 l 45 -45 l 371 371 l 371 -371 l 45 45 z"
          ></path>
          <path
            type="path"
            style={{ fill: '#FAFAFAFF' }}
            d="M 1386 1696 h -1226 v -1344 h 1728 v 842 l -256 256 l -371 -371 l -181 181 l 371 371 z"
          ></path>
          <path
            type="path"
            style={{ fill: '#797774FF' }}
            d="M 1888 832 h -544 v 330 l -64 -64 v -266 h -512 v 384 h 357 l -45 44 l 20 20 h -332 v 416 h -64 v -416 h -544 v -64 h 544 v -384 h -544 v -64 h 544 v -416 h 64 v 416 h 512 v -422 h 64 v 422 h 544 z"
          ></path>
          <path
            type="path"
            style={{ fill: '#3A3A38FF' }}
            d="M 1354 1728 h -1226 v -1408 h 1792 v 842 l -64 64 v -842 h -1664 v 1280 h 1226 z"
          ></path>
          <path
            type="path"
            style={{ fill: '#ED3D3BFF' }}
            d="M 1677 1632 l 371 371 l -45 45 l -371 -371 l -371 371 l -45 -45 l 371 -371 l -371 -371 l 45 -45 l 371 371 l 371 -371 l 45 45 z"
          ></path>
        </svg>
      }
      tooltip={showTooltip && 'Delete Options'}
      disabled={!canExecute.deleteTable && !canExecute.deleteColumn && !canExecute.deleteRow}
    >
      Delete
    </ToolbarMenuButton>
  );
  const deleteOptions = (
    <DialogTrigger disableButtonEnhancement>
      <MenuList>
        <ToolbarMenuItem
          icon={
            <svg height="20" width="20" viewBox="0,0,2048,2048" focusable="false">
              <path
                type="path"
                style={{ fill: 'transparent' }}
                d="M 1354 1728 h -1226 v -1408 h 1792 v 842 l -64 64 v -394 h -512 v 330 l -64 -64 v -266 h -512 v 384 h 357 l -45 44 l 20 20 h -332 v 384 h 650 m -714 -832 h -512 v 384 h 512 m 576 -448 v -384 h -512 v 384 m 1088 -384 h -512 v 384 h 512 m -1664 -384 v 384 h 512 v -384 m -512 1280 h 512 v -384 h -512 m 1485 352 l 371 371 l -45 45 l -371 -371 l -371 371 l -45 -45 l 371 -371 l -371 -371 l 45 -45 l 371 371 l 371 -371 l 45 45 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#FAFAFAFF' }}
                d="M 1386 1696 h -1226 v -1344 h 1728 v 842 l -256 256 l -371 -371 l -181 181 l 371 371 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#797774FF' }}
                d="M 1888 832 h -544 v 330 l -64 -64 v -266 h -512 v 384 h 357 l -45 44 l 20 20 h -332 v 416 h -64 v -416 h -544 v -64 h 544 v -384 h -544 v -64 h 544 v -416 h 64 v 416 h 512 v -422 h 64 v 422 h 544 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#3A3A38FF' }}
                d="M 1354 1728 h -1226 v -1408 h 1792 v 842 l -64 64 v -842 h -1664 v 1280 h 1226 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#ED3D3BFF' }}
                d="M 1677 1632 l 371 371 l -45 45 l -371 -371 l -371 371 l -45 -45 l 371 -371 l -371 -371 l 45 -45 l 371 371 l 371 -371 l 45 45 z"
              ></path>
            </svg>
          }
          disabled={!canExecute.deleteTable}
          onClick={() => editor.chain().focus().deleteTable().run()}
        >
          Delete Table
        </ToolbarMenuItem>
        <ToolbarMenuItem
          icon={
            <svg height="20" width="20" viewBox="0,0,2048,2048" focusable="false">
              <path
                type="path"
                style={{ fill: 'transparent' }}
                d="M 614 922 h -409 v -820 h 102 v 717 h 307 m 1127 -717 v 820 h -410 v -103 h 307 v -717 m -307 1288 l -300 300 l 300 300 l -58 58 l -300 -300 l -300 300 l -59 -58 l 301 -300 l -301 -300 l 59 -59 l 300 300 l 300 -300 m -556 -173 v -1056 h 512 v 1056 l -256 256 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#FAFAFAFF' }}
                d="M 614 870 h -358 v -768 h 358 m 1076 768 h -359 v -768 h 359 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#3A3A38FF' }}
                d="M 205 102 h 102 v 717 h 307 v 103 h -409 m 1536 0 h -410 v -103 h 307 v -717 h 103 z"
              ></path>
              <path type="path" style={{ fill: '#DCEDFA' }} d="M 768 1209 v -1107 h 410 v 1107 l -205 205 z"></path>
              <path
                type="path"
                style={{ fill: '#0063B1FF' }}
                d="M 717 1158 v -1056 h 102 v 1159 m 307 0 v -1159 h 103 v 1056 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#ED3D3BFF' }}
                d="M 973 1631 l 300 -300 l 58 59 l -300 300 l 300 300 l -58 58 l -300 -300 l -300 300 l -59 -58 l 301 -300 l -301 -300 l 59 -59 z"
              ></path>
            </svg>
          }
          disabled={!canExecute.deleteColumn}
          onClick={() => editor.chain().focus().deleteColumn().run()}
        >
          Delete Column
        </ToolbarMenuItem>
        <ToolbarMenuItem
          icon={
            <svg height="20" width="20" viewBox="0,0,2048,2048" focusable="false">
              <path
                type="path"
                style={{ fill: 'transparent' }}
                d="M 819 307 h -717 v -102 h 820 v 409 h -103 m 0 717 h 103 v 410 h -820 v -103 h 717 m 929 -665 l 300 300 l -58 58 l -300 -300 l -300 300 l -59 -58 l 300 -300 l -300 -300 l 59 -59 l 300 301 l 300 -301 l 58 59 m -634 300 l -256 256 h -1056 v -512 h 1056 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#FAFAFAFF' }}
                d="M 102 614 v -358 h 768 v 358 m -768 1076 v -359 h 768 v 359 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#3A3A38FF' }}
                d="M 922 205 v 409 h -103 v -307 h -717 v -102 m 0 1536 v -103 h 717 v -307 h 103 v 410 z"
              ></path>
              <path type="path" style={{ fill: '#DCEDFA' }} d="M 1209 1178 h -1107 v -410 h 1107 l 205 205 z"></path>
              <path
                type="path"
                style={{ fill: '#0063B1FF' }}
                d="M 1158 1229 h -1056 v -103 h 1159 m 0 -307 h -1159 v -102 h 1056 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#ED3D3BFF' }}
                d="M 1331 673 l 59 -59 l 300 301 l 300 -301 l 58 59 l -300 300 l 300 300 l -58 58 l -300 -300 l -300 300 l -59 -58 l 300 -300 z"
              ></path>
            </svg>
          }
          disabled={!canExecute.deleteRow}
          onClick={() => editor.chain().focus().deleteRow().run()}
        >
          Delete Row
        </ToolbarMenuItem>
      </MenuList>
    </DialogTrigger>
  );

  // insert:
  const insertButton = (
    <ToolbarMenuButton
      icon={
        <svg height="20" width="20" viewBox="0,0,2048,2048" focusable="false">
          <path
            type="path"
            style={{ fill: 'transparent' }}
            d="M 1946 205 v 1638 h -1844 v -1638 h 559 l -102 102 h -354 v 410 h 266 l 102 103 h 51 l 100 -103 h 95 v 409 h 307 v -409 h 96 l 101 102 h 50 l 102 -102 h 368 v -410 h -456 l -102 -102 m -466 1126 v 410 h 410 v -410 m -1024 410 h 512 v -410 h -512 m 1638 410 v -410 h -512 v 410 m -409 -717 v -718 l -323 323 l -72 -73 l 446 -446 l 446 446 l -73 73 l -322 -323 v 718 z"
          ></path>
          <path
            type="path"
            style={{ fill: '#FAFAFAFF' }}
            d="M 1894 256 v 1536 h -1740 v -1536 h 456 l -300 300 l 289 290 l 220 -228 v 508 h 307 v -508 l 220 228 l 290 -290 l -300 -300 z"
          ></path>
          <path
            type="path"
            style={{ fill: '#797774FF' }}
            d="M 1331 1280 v 514 h -102 v -514 m -410 0 v 514 h -102 v -514 z"
          ></path>
          <path
            type="path"
            style={{ fill: '#83BEECFF' }}
            d="M 1894 768 v 512 h -1740 v -512 h 368 l 52 52 h 50 l 50 -52 h 145 v 358 h 307 v -358 h 145 l 52 52 h 49 l 52 -52 z"
          ></path>
          <path
            type="path"
            style={{ fill: '#0063B1FF' }}
            d="M 1888 1331 h -1739 v -102 h 1739 m -567 -410 h -195 v -102 h 96 m 683 0 v 102 h -532 l 102 -102 m -902 102 h -421 v -102 h 319 m 253 0 h 95 v 102 h -194 z"
          ></path>
          <path
            type="path"
            style={{ fill: '#3A3A38FF' }}
            d="M 1946 205 v 1638 h -1844 v -1638 h 559 l -102 102 h -354 v 1434 h 1638 v -1434 h -456 l -102 -102 z"
          ></path>
          <path
            type="path"
            style={{ fill: '#3A3A38FF' }}
            d="M 527 556 l 446 -446 l 446 446 l -73 73 l -322 -323 v 718 h -102 v -718 l -323 323 z"
          ></path>
        </svg>
      }
      tooltip={showTooltip && 'Insert Options'}
      disabled={
        !canExecute.addRowBefore && !canExecute.addRowAfter && !canExecute.addColumnBefore && !canExecute.addColumnAfter
      }
    >
      Insert
    </ToolbarMenuButton>
  );
  const insertOptions = (
    <DialogTrigger disableButtonEnhancement>
      <MenuList>
        <ToolbarMenuItem
          icon={
            <svg height="20" width="20" viewBox="0,0,2048,2048" focusable="false">
              <path
                type="path"
                style={{ fill: 'transparent' }}
                d="M 1946 205 v 1638 h -1844 v -1638 h 559 l -102 102 h -354 v 410 h 266 l 102 103 h 51 l 100 -103 h 95 v 409 h 307 v -409 h 96 l 101 102 h 50 l 102 -102 h 368 v -410 h -456 l -102 -102 m -466 1126 v 410 h 410 v -410 m -1024 410 h 512 v -410 h -512 m 1638 410 v -410 h -512 v 410 m -409 -717 v -718 l -323 323 l -72 -73 l 446 -446 l 446 446 l -73 73 l -322 -323 v 718 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#FAFAFAFF' }}
                d="M 1894 256 v 1536 h -1740 v -1536 h 456 l -300 300 l 289 290 l 220 -228 v 508 h 307 v -508 l 220 228 l 290 -290 l -300 -300 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#797774FF' }}
                d="M 1331 1280 v 514 h -102 v -514 m -410 0 v 514 h -102 v -514 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#83BEECFF' }}
                d="M 1894 768 v 512 h -1740 v -512 h 368 l 52 52 h 50 l 50 -52 h 145 v 358 h 307 v -358 h 145 l 52 52 h 49 l 52 -52 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#0063B1FF' }}
                d="M 1888 1331 h -1739 v -102 h 1739 m -567 -410 h -195 v -102 h 96 m 683 0 v 102 h -532 l 102 -102 m -902 102 h -421 v -102 h 319 m 253 0 h 95 v 102 h -194 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#3A3A38FF' }}
                d="M 1946 205 v 1638 h -1844 v -1638 h 559 l -102 102 h -354 v 1434 h 1638 v -1434 h -456 l -102 -102 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#3A3A38FF' }}
                d="M 527 556 l 446 -446 l 446 446 l -73 73 l -322 -323 v 718 h -102 v -718 l -323 323 z"
              ></path>
            </svg>
          }
          disabled={!canExecute.addRowBefore}
          onClick={() => editor.chain().focus().addRowBefore().run()}
        >
          Insert Row Above
        </ToolbarMenuItem>
        <ToolbarMenuItem
          icon={
            <svg height="20" width="20" viewBox="0,0,2048,2048" focusable="false">
              <path
                type="path"
                style={{ fill: 'transparent' }}
                d="M 1946 205 v 1638 h -661 l 102 -102 h 456 v -410 h -368 l -102 -102 h -52 l -99 102 h -96 v -409 h -307 v 409 h -95 l -99 -102 h -52 l -102 102 h -266 v 410 h 354 l 102 102 h -559 v -1638 m 1127 512 v -410 h -410 v 410 m 1024 -410 h -512 v 410 h 512 m -1638 -410 v 410 h 512 v -410 m 702 1185 l -446 446 l -446 -446 l 72 -73 l 323 323 v -718 h 102 v 718 l 322 -323 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#FAFAFAFF' }}
                d="M 1894 256 v 1536 h -558 l 300 -300 l -290 -290 l -220 228 v -508 h -307 v 508 l -220 -228 l -289 290 l 300 300 h -456 v -1536 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#797774FF' }}
                d="M 1229 761 v -510 h 102 v 510 m -614 0 v -510 h 102 v 510 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#83BEECFF' }}
                d="M 1894 768 v 512 h -470 l -51 -51 h -52 l -50 51 h -145 v -358 h -307 v 358 h -145 l -49 -51 h -52 l -51 51 h -368 v -512 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#0063B1FF' }}
                d="M 1894 819 h -1756 v -102 h 1756 m -672 614 h -96 v -102 h 195 m 566 0 v 102 h -412 l -102 -102 m -748 0 h 194 v 102 h -95 m -253 0 h -306 v -102 h 408 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#3A3A38FF' }}
                d="M 1946 205 v 1638 h -661 l 102 -102 h 456 v -1434 h -1638 v 1434 h 354 l 102 102 h -559 v -1638 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#3A3A38FF' }}
                d="M 1419 1492 l -446 446 l -446 -446 l 72 -73 l 323 323 v -718 h 102 v 718 l 322 -323 z"
              ></path>
            </svg>
          }
          disabled={!canExecute.addRowAfter}
          onClick={() => editor.chain().focus().addRowAfter().run()}
        >
          Insert Row Below
        </ToolbarMenuItem>
        <ToolbarMenuItem
          icon={
            <svg height="20" width="20" viewBox="0,0,2048,2048" focusable="false">
              <path
                type="path"
                style={{ fill: 'transparent' }}
                d="M 1946 205 v 1638 h -1844 v -661 l 103 103 v 456 h 512 v -266 l 128 -128 l -128 -130 v -91 h 409 v -307 h -409 v -91 l 128 -128 l -128 -130 v -163 h -512 v 354 l -103 102 v -558 m 1741 102 h -512 v 410 h 512 m -512 512 h 512 v -410 h -512 m 512 922 v -410 h -512 v 410 m -702 -1142 l -323 323 h 718 v 102 h -718 l 323 322 l -73 73 l -446 -446 l 446 -446 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#FAFAFAFF' }}
                d="M 1894 256 v 1536 h -1740 v -559 l 402 403 l 290 -290 l -220 -220 h 500 v -307 h -500 l 220 -220 l -290 -289 l -402 402 v -456 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#797774FF' }}
                d="M 1894 819 h -614 v -102 h 614 m 0 614 h -614 v -102 h 614 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#DCEDFA' }}
                d="M 1280 256 v 1536 h -512 v -368 l 78 -78 l -78 -77 v -143 h 358 v -307 h -358 v -142 l 78 -78 l -78 -77 v -266 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#0063B1FF' }}
                d="M 1331 1792 h -102 v -1536 h 102 m -512 370 v 193 h -102 v -91 m 102 592 l -102 -103 v -91 h 102 m 0 247 v 419 h -102 v -317 m 0 -1005 v -214 h 102 v 317 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#3A3A38FF' }}
                d="M 1946 205 v 1638 h -1844 v -661 l 103 103 v 456 h 1638 v -1434 h -1638 v 354 l -103 102 v -558 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#3A3A38FF' }}
                d="M 629 599 l -323 323 h 718 v 102 h -718 l 323 322 l -73 73 l -446 -446 l 446 -446 z"
              ></path>
            </svg>
          }
          disabled={!canExecute.addColumnBefore}
          onClick={() => editor.chain().focus().addColumnBefore().run()}
        >
          Insert Column Left
        </ToolbarMenuItem>
        <ToolbarMenuItem
          icon={
            <svg height="20" width="20" viewBox="0,0,2048,2048" focusable="false">
              <path
                type="path"
                style={{ fill: 'transparent' }}
                d="M 1946 763 l -103 -102 v -354 h -511 v 164 l -130 128 l 129 129 v 91 h -409 v 307 h 410 v 92 l -130 128 l 129 129 v 266 h 512 v -456 l 103 -103 v 661 h -1844 v -1638 h 1844 m -1229 614 h -512 v 410 h 512 m -512 -922 v 410 h 512 v -410 m -512 1434 h 512 v -410 h -512 m 1214 15 l 323 -322 h -718 v -102 h 718 l -323 -323 l 73 -72 l 446 446 l -446 446 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#FAFAFAFF' }}
                d="M 1894 256 v 456 l -402 -402 l -290 289 l 220 220 h -500 v 307 h 500 l -220 220 l 290 290 l 402 -403 v 559 h -1740 v -1536 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#797774FF' }}
                d="M 154 717 h 614 v 102 h -614 m 0 410 h 614 v 102 h -614 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#DCEDFA' }}
                d="M 1280 256 v 266 l -78 77 l 78 78 v 142 h -358 v 307 h 358 v 143 l -78 77 l 78 78 v 368 h -512 v -1536 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#0063B1FF' }}
                d="M 717 256 h 102 v 1536 h -102 m 614 -1064 v 91 h -102 v -193 m 0 500 h 102 v 91 l -102 103 m 102 155 v 317 h -102 v -419 m 0 -800 v -317 h 102 v 214 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#3A3A38FF' }}
                d="M 1946 205 v 558 l -103 -102 v -354 h -1638 v 1434 h 1638 v -456 l 103 -103 v 661 h -1844 v -1638 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#3A3A38FF' }}
                d="M 1419 1346 l 323 -322 h -718 v -102 h 718 l -323 -323 l 73 -72 l 446 446 l -446 446 z"
              ></path>
            </svg>
          }
          disabled={!canExecute.addColumnAfter}
          onClick={() => editor.chain().focus().addColumnAfter().run()}
        >
          Insert Column Right
        </ToolbarMenuItem>
      </MenuList>
    </DialogTrigger>
  );

  const deleteMenu = showDialog ? (
    <Dialog>
      <DialogTrigger disableButtonEnhancement>{deleteButton}</DialogTrigger>
      <DialogSurface className="w-fit">{deleteOptions}</DialogSurface>
    </Dialog>
  ) : (
    <Menu>
      <MenuTrigger>{deleteButton}</MenuTrigger>
      <MenuPopover>{deleteOptions}</MenuPopover>
    </Menu>
  );

  const insertMenu = showDialog ? (
    <Dialog>
      <DialogTrigger disableButtonEnhancement>{insertButton}</DialogTrigger>
      <DialogSurface className="w-fit">{insertOptions}</DialogSurface>
    </Dialog>
  ) : (
    <Menu>
      <MenuTrigger>{insertButton}</MenuTrigger>
      <MenuPopover>{insertOptions}</MenuPopover>
    </Menu>
  );

  const mergeButton = (
    <ToolbarButton
      icon={
        <svg height="20" width="20" viewBox="0,0,2048,2048" focusable="false">
          <path
            type="path"
            style={{ fill: 'transparent' }}
            d="M 128 128 h 1728 v 1728 h -1728 m 64 -1664 v 512 h 768 v -512 m 832 576 h -1600 v 448 h 1600 m -1600 576 h 768 v -512 h -768 m 1600 512 v -512 h -768 v 512 m 0 -1088 h 768 v -512 h -768 z"
          ></path>
          <path type="path" style={{ fill: '#FAFAFAFF' }} d="M 160 160 h 1664 v 1664 h -1664 z"></path>
          <path
            type="path"
            style={{ fill: '#797774FF' }}
            d="M 960 704 v -512 h 64 v 512 m -64 1152 v -576 h 64 v 576 z"
          ></path>
          <path
            type="path"
            style={{ fill: '#3A3A38FF' }}
            d="M 128 128 h 1728 v 1728 h -1728 m 1664 -64 v -1600 h -1600 v 1600 z"
          ></path>
          <path
            type="path"
            style={{ fill: '#0063B1FF' }}
            d="M 1856 1280 h -1728 v -576 h 1728 m -1664 512 h 1600 v -448 h -1600 z"
          ></path>
        </svg>
      }
      tooltip={showTooltip && 'Merge Cells'}
      disabled={(editor) => !editor.can().mergeCells()}
      onClick={() => editor.chain().focus().mergeCells().run()}
    >
      Merge
    </ToolbarButton>
  );

  const splitButton = (
    <ToolbarButton
      icon={
        <svg height="20" width="20" viewBox="0,0,2048,2048" focusable="false">
          <path
            type="path"
            style={{ fill: 'transparent' }}
            d="M 128 128 h 1728 v 1728 h -1728 m 64 -1664 v 512 h 768 v -512 m 512 576 v 448 h 320 v -448 m -384 448 v -448 h -384 v 448 m -512 0 v -448 h -320 v 448 m 384 -448 v 448 h 384 v -448 m -768 1024 h 768 v -512 h -768 m 1600 512 v -512 h -768 v 512 m 0 -1088 h 768 v -512 h -768 z"
          ></path>
          <path type="path" style={{ fill: '#FAFAFAFF' }} d="M 160 160 h 1664 v 1664 h -1664 z"></path>
          <path
            type="path"
            style={{ fill: '#797774FF' }}
            d="M 960 704 v -512 h 64 v 512 m -64 1152 v -576 h 64 v 576 z"
          ></path>
          <path
            type="path"
            style={{ fill: '#3A3A38FF' }}
            d="M 128 128 h 1728 v 1728 h -1728 m 1664 -64 v -1600 h -1600 v 1600 z"
          ></path>
          <path
            type="path"
            style={{ fill: '#0063B1FF' }}
            d="M 128 704 h 1728 v 576 h -1728 m 64 -64 h 320 v -448 h -320 m 384 448 h 384 v -448 h -384 m 448 448 h 384 v -448 h -384 m 768 448 v -448 h -320 v 448 z"
          ></path>
        </svg>
      }
      tooltip={showTooltip && 'Split Cell'}
      disabled={(editor) => !editor.can().splitCell()}
      onClick={() => editor.chain().focus().splitCell().run()}
    >
      Split
    </ToolbarButton>
  );

  const headerButton = (
    <ToolbarToggleButton
      selector={(editor) => {
        if (editor.can().mergeCells()) {
          // multi select:
          const selectedContent = JSON.stringify(editor.view.state.selection.content().content);
          return selectedContent.includes('tableHeader');
        }
        return editor.isActive('tableHeader');
      }}
      icon={
        <svg height="20" width="20" viewBox="0,0,2048,2048" focusable="false">
          <path
            type="path"
            style={{ fill: 'transparent' }}
            d="M 102 102 h 1844 v 1844 h -1844 m 103 -1741 v 205 h 1638 v -205 m -1024 1126 h 410 v -307 h -410 m 410 410 h -410 v 409 h 410 m -512 -512 v -307 h -512 v 307 m 1024 -819 h -410 v 410 h 410 m 102 102 v 307 h 512 v -307 m -512 -102 h 512 v -410 h -512 m -614 0 h -512 v 410 h 512 m -512 512 v 409 h 512 v -409 m 614 409 h 512 v -409 h -512 z"
          ></path>
          <path type="path" style={{ fill: '#FAFAFAFF' }} d="M 1894 1894 h -1740 v -1433 h 1740 z"></path>
          <path type="path" style={{ fill: '#C8C6C4FF' }} d="M 1894 461 h -1740 v -307 h 1740 z"></path>
          <path
            type="path"
            style={{ fill: '#3A3A38FF' }}
            d="M 102 102 h 1844 v 1844 h -1844 m 1741 -103 v -1331 h -1638 v 1331 m 1638 -1433 v -205 h -1638 v 205 z"
          ></path>
        </svg>
      }
      tooltip={showTooltip && 'Toggle Table Header'}
      disabled={(editor) => !editor.can().toggleHeaderCell()}
      onClick={() => editor.chain().focus().toggleHeaderCell().run()}
    ></ToolbarToggleButton>
  );

  const textDirectionButton = (
    <ToolbarButton
      icon={isTextRotated ? <TextDirectionHorizontalRightRegular /> : <TextDirectionVerticalRegular />}
      tooltip={showTooltip && 'Text Direction'}
      onClick={() => {
        editor
          .chain()
          .focus()
          .setCellAttribute('textRotation', isTextRotated ? null : 'btl')
          .run();
      }}
    ></ToolbarButton>
  );

  const colorButton = <ColorCellButton editor={editor} showTooltip={showTooltip} isBubble={isBubble} />;

  const bordersButton = (
    <BordersMenuButton editor={editor} showDialog={showDialog} showTooltip={showTooltip} isBubble={isBubble} />
  );

  const alignmentButton = <CellAlignmentMenuButton editor={editor} showTooltip={showTooltip} />;

  const captionButton = <InsertCaptionButton />;

  if (isBubble) {
    return (
      <>
        {deleteMenu}
        {insertMenu}
        <ToolbarDivider />
        {mergeButton}
        {splitButton}
        <ToolbarDivider />
        {headerButton}
        {colorButton}
        {bordersButton}
        {alignmentButton}
        {textDirectionButton}
        <ToolbarDivider />
        {captionButton}
      </>
    );
  }

  const params: TOverflowNodeParam[] = [
    { node: deleteMenu },
    { node: insertMenu },
    { node: <ToolbarDivider />, hideInMenu: true },
    { node: mergeButton },
    { node: splitButton },
    { node: <ToolbarDivider />, hideInMenu: true },
    { node: headerButton },
    { node: colorButton },
    { node: bordersButton },
    { node: alignmentButton },
    { node: textDirectionButton },
    { node: <ToolbarDivider />, hideInMenu: true },
    { node: captionButton },
  ];

  return (
    <div className="w-full">
      <Overflow
        params={params}
        defaultAppearance="secondary"
        label="More"
        menuButtonClassname={mergeClasses(classes.root, classes.reducePadding)}
      />
    </div>
  );
};

export default TableNodeTabContent;
